(function () {

  'use strict';

  angular.module('PrintixProviderIdp', [])

    .constant('IDP_PROVIDERS', [
      // Azure/Google use per-variant card directives (pxAzure/pxGoogle) instead of an
      // in-menu connect form. formTemplate stays null; their card directives mount via
      // <px-azure> / <px-google> in authenticationSettingsView.html.
      // capabilities are variant-level metadata (this provider type supports the concept);
      // per-directory link existence is the runtime gate (this specific directory has it
      // granted) — both signals are needed.
      {
        type: 'azure',
        caption: 'Azure',
        helpCaption: 'HelpAuthenticationAzure',
        logoTemplate: 'app/templates/azureLogo.template.html',
        redirectURIPath: null,
        showRedirectURI: false,
        formTemplate: null,
        summaryTemplate: 'app/components/authenticationSettings/azure/azureSummary.template.html',
        errorHelp: {
          codes: [],
          manualLink: null
        },
        capabilities: {
          connect: true,
          acceptForAllUsers: true,
          synchronizeGroups: true,
          scim: true,
          universalPrint: true,
          sharepoint: true,
          oneDrive: true,
          globalAdminRestriction: true,
          requireIdpLoginToken: true
        }
      },
      {
        type: 'google',
        caption: 'Google',
        helpCaption: 'HelpAuthenticationGoogle',
        logoTemplate: 'app/templates/googleLogo.template.html',
        redirectURIPath: null,
        showRedirectURI: false,
        formTemplate: null,
        summaryTemplate: 'app/components/authenticationSettings/google/googleSummary.template.html',
        errorHelp: {
          codes: [],
          manualLink: null
        },
        capabilities: {
          connect: true,
          acceptForAllUsers: true,
          synchronizeGroups: true,
          scim: true
        }
      },
      {
        type: 'oidc',
        caption: 'OIDC',
        helpCaption: 'HelpAuthenticationOIDC',
        // logoTemplate: oidcLogo.template.html does not exist on disk; default to null per §6 Q2
        logoTemplate: null,
        redirectURIPath: '/identity-providers/oidc/signin/responseCode',
        // Q1 default: missed feature; flip to false if dead-code verdict lands
        showRedirectURI: true,
        formTemplate: 'app/components/authenticationSettings/oidc/oidcForm/oidcFormHost.template.html',
        summaryTemplate: 'app/components/authenticationSettings/oidc/oidcSummary.template.html',
        errorHelp: {
          codes: ['INVALID_OIDC_DOMAIN', 'DUPLICATE_DIRECTORY'],
          manualLink: 'https://docshield.tungstenautomation.com/Printix/en_US/help/admin/Printix_admin/t_oidc_auth.html'
        }
      },
      {
        type: 'okta',
        caption: 'Okta',
        helpCaption: 'HelpAuthenticationOkta',
        logoTemplate: 'app/templates/oktaLogo.template.html',
        redirectURIPath: '/identity-providers/okta/signin/responseCode',
        showRedirectURI: true,
        formTemplate: 'app/components/authenticationSettings/okta/oktaForm/oktaFormHost.template.html',
        summaryTemplate: 'app/components/authenticationSettings/okta/oktaSummary.template.html',
        errorHelp: {
          codes: ['INVALID_OIDC_DOMAIN', 'DUPLICATE_DIRECTORY'],
          manualLink: 'https://docshield.tungstenautomation.com/Printix/en_US/help/admin/Printix_admin/t_okta_auth.html'
        }
      },
      {
        type: 'onelogin',
        caption: 'OneLogin',
        helpCaption: 'HelpAuthenticationOneLogin',
        logoTemplate: 'app/templates/oneloginLogo.template.html',
        redirectURIPath: '/identity-providers/onelogin/signin/response',
        showRedirectURI: true,
        formTemplate: 'app/components/authenticationSettings/oneLogin/oneLoginForm/oneLoginFormHost.template.html',
        summaryTemplate: 'app/components/authenticationSettings/oneLogin/oneLoginSummary.template.html',
        errorHelp: {
          codes: ['INVALID_OIDC_DOMAIN', 'DUPLICATE_DIRECTORY'],
          manualLink: null
        }
      }
    ])

    .constant('PROVIDER_LOGO_TEMPLATE', {
        azure: 'app/templates/azureLogo.template.html',
        google: 'app/templates/googleLogo.template.html',
        oidc: '',
        okta: 'app/templates/oktaLogo.template.html',
        onelogin: 'app/templates/oneloginLogo.template.html',
      }
    )

    // group.info.provider → tenantIdentityProviders.providers[<type>] key.
    // Pre-existing inline switches in group-provider.js and printQueueProperties.provider.js
    // encode the same mapping; this constant is the single source for new call sites.
    .constant('GROUP_PROVIDER_TO_IDP_TYPE', {
      AZURE_AD: 'azure',
      GOOGLE_GSUITE: 'google'
    })

    .service('IdpProviderService', IdpProviderService)

    .factory('IdpContextMenuMixin', IdpContextMenuMixin);

  IdpProviderService.$inject = ['$http', '$rootScope', '$filter', 'toastr', 'EnvironmentService', 'EventServiceEvents', 'IDP_PROVIDERS', 'PROVIDER_LOGO_TEMPLATE'];

  function IdpProviderService($http, $rootScope, $filter, toastr, EnvironmentService, EventServiceEvents, IDP_PROVIDERS, PROVIDER_LOGO_TEMPLATE) {
    const self = this;

    self.descriptors = IDP_PROVIDERS;

    self.descriptorByType = function (type) {
      return IDP_PROVIDERS.find(function (descriptor) {
        return descriptor.type === type;
      }) || null;
    };

    // Per-directory group-sync state. Mutually exclusive — encodes the four
    // UI affordances the scim row renders, plus a NONE bail-out.
    // SCIM links are state-bearing (px:enable_scim_provisioning is consumed
    // on use, replaced by px:scim_provisioning). The legacy px:enable_groups
    // link is permanent; its boolean state lives on info.groups_enabled.
    self.groupSyncStateOf = function (info) {
      const links = (info && info._links) || {};
      // Enabled states win over available states — "Get started" must not
      // light up when sync is already running via either mechanism.
      if (links['px:scim_provisioning']) {
        return 'SCIM_ENABLED';
      }
      if (links['px:enable_groups'] && info && info.groups_enabled) {
        return 'LEGACY_ENABLED';
      }
      if (links['px:enable_scim_provisioning']) {
        return 'SCIM_AVAILABLE';
      }
      if (links['px:enable_groups']) {
        return 'LEGACY_AVAILABLE';
      }
      return 'NONE';
    };

    self.allDirectories = function (tenant) {
      const providers = tenant?.info?._embedded?.tenantIdentityProviders?.providers || {};
      const list = [];
      for (const type in providers) {
        if (!Object.prototype.hasOwnProperty.call(providers, type)) continue;
        const perType = self.directoriesFor(tenant, type);
        for (const d of perType) {
          if (d.unConnected) continue;
          list.push(Object.assign({ providerType: type, logoTemplate: PROVIDER_LOGO_TEMPLATE[type] }, d));
        }
      }
      return list;
    };

    self.directoriesFor = function (tenant, type) {
      const directoriesMap = tenant?.info?._embedded?.tenantIdentityProviders?.providers?.[type]?.directories;
      const list = [];
      if (directoriesMap) {
        for (const key in directoriesMap) {
          if (Object.prototype.hasOwnProperty.call(directoriesMap, key)) {
            const info = angular.copy(directoriesMap[key]);
            const directory = {
              name: key,
              unConnected: false,
              info,
              groupSyncState: self.groupSyncStateOf(info),
              scimConfig: null
            };
            list.push(directory);
            const scimLink = info._links?.['px:scim_provisioning'];
            if (scimLink?.href) {
              // Background enrichment fired while rendering the directory list.
              // Swallow rejections here: a transient SCIM endpoint failure would
              // otherwise cascade into one px-error-help dialog per directory on
              // tab-open. User-initiated SCIM actions still route through
              // handleScimError so deliberate failures stay visible.
              self.getScimRegistration(scimLink.href).then(function (response) {
                directory.scimConfig = response.data;
              }).catch(angular.noop);
            }
          }
        }
      }
      if (list.length <= 0) {
        list.push({ name: null, unConnected: true, info: {}, groupSyncState: 'NONE', scimConfig: null });
      }
      return list;
    };

    self.providerSelfHref = function (tenant, type) {
      return tenant.info._embedded.tenantIdentityProviders.providers[type]._links.self.href;
    };

    self.redirectURIFor = function (descriptor) {
      if (!descriptor || !descriptor.redirectURIPath) {
        return null;
      }
      return EnvironmentService.authBaseUrl + descriptor.redirectURIPath;
    };

    self.copyValue = function (value) {
      const input = document.createElement('input');
      input.setAttribute('value', value);
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toastr.success($filter('translate')('CopyToClipboard'));
    };

    self.copyRedirectURI = function (descriptor) {
      self.copyValue(self.redirectURIFor(descriptor));
    };

    self.tokenEndpoint = function () {
      return EnvironmentService.authBaseUrl + '/oauth/token';
    };

    self.enableScim = function (enableScimUrl, authType) {
      // The HATEOAS link already carries ?authType=CLIENT_CREDENTIALS by
      // default; replace whatever's there with the user's pick instead of
      // appending a second ?authType= and producing an invalid URL.
      const baseUrl = enableScimUrl.split('?')[0];
      const param = (authType === 'BEARER_TOKEN') ? 'BEARER_TOKEN' : 'CLIENT_CREDENTIALS';
      return $http.post(baseUrl + '?authType=' + param, null, {
        headers: { Accept: 'application/json' }
      });
    };

    self.getScimRegistration = function (scimUrl) {
      return $http.get(scimUrl, {
        headers: { Accept: 'application/json' }
      });
    };

    self.disableScim = function (scimUrl) {
      return $http.delete(scimUrl, {
        headers: { Accept: 'application/json' }
      });
    };

    self.handleScimError = function (error) {
      const wrapped = { data: null };
      try {
        const has = Object.prototype.hasOwnProperty;
        if (error && error.data &&
            has.call(error.data, 'errorCode') &&
            has.call(error.data, 'parameterizedErrorText') &&
            has.call(error.data, 'parameters') &&
            has.call(error.data, 'errorText')) {
          wrapped.data = error.data;
        } else {
          throw new Error('(error.data) is missing required attributes');
        }
      } catch (e) {
        wrapped.data = {
          errorCode: 'UNKNOWN_ERROR',
          parameterizedErrorText: 'Unknown error',
          parameters: [],
          errorText: 'Unknown error'
        };
      }
      toastr.error($filter('translate')('ERROR'));
      $rootScope.$broadcast('REQUEST_ERROR_HELP', { error: wrapped });
    };

    self.create = function (tenant, type, payload) {
      // Fire-and-forget refresh path — server emits IDENTITY_PROVIDER_CREATED;
      // attachListeners' handler calls refreshDirectory.
      return $http.post(self.providerSelfHref(tenant, type), payload);
    };

    self.handleCreateError = function (error, descriptor, tenant) {
      // Centralized POST-catch dispatch shared by all in-scope form directives.
      // Guards against null `error.data` (e.g., raw 400 with no body), maps known
      // error codes to REQUEST_ERROR_HELP, falls through to a generic toastr.
      if (!error || !error.data) {
        toastr.error($filter('translate')('ERROR'));
        return;
      }
      error.data.manualLink = descriptor.errorHelp.manualLink;

      switch (error.data.errorCode) {
        case 'INVALID_OIDC_DOMAIN':
          error.data.errorCaptionOverride = 'UnVerifiedDomain';
          $rootScope.$broadcast('REQUEST_ERROR_HELP', { error });
          break;
        case 'DUPLICATE_DIRECTORY':
          error.data.parameters[1] = tenant.info.hostingDomains[0];
          $rootScope.$broadcast('REQUEST_ERROR_HELP', { error });
          break;
        case 'VALIDATION_FAILED':
          // Surface the first fieldError so the help dialog shows actionable
          // detail (e.g., "oidcDiscoveryUrl: must be a valid URL") instead of
          // the generic "The request was not valid."
          if (angular.isArray(error.data.fieldErrors) && error.data.fieldErrors.length > 0) {
            const fe = error.data.fieldErrors[0];
            error.data.parameterizedErrorText = fe.field + ': ' + fe.defaultMessage;
            error.data.errorText = error.data.parameterizedErrorText;
          }
          $rootScope.$broadcast('REQUEST_ERROR_HELP', { error });
          break;
        default:
          toastr.error($filter('translate')('ERROR'));
      }
    };

    self.refreshDirectory = function (tenant, type, directoryUrl) {
      return $http.get(directoryUrl).then(function (response) {
        const providers = tenant.info._embedded.tenantIdentityProviders.providers;
        if (!providers[type].directories) {
          providers[type].directories = {};
        }
        providers[type].directories[response.data.name] = response.data;
      });
    };

    self.deleteDirectory = function (directoryUrl) {
      return $http.delete(directoryUrl);
    };

    self.removeDirectory = function (tenant, type, directoryUrl) {
      const directoriesMap = tenant.info._embedded.tenantIdentityProviders.providers[type].directories;
      if (!directoriesMap) {
        return;
      }
      for (const key in directoriesMap) {
        if (Object.prototype.hasOwnProperty.call(directoriesMap, key)) {
          const entry = directoriesMap[key];
          if (entry && entry._links && entry._links.self && entry._links.self.href === directoryUrl) {
            delete directoriesMap[key];
          }
        }
      }
    };

    self.attachListeners = function (scope, type) {
      const events = EventServiceEvents.CHANNELS.ALL_TENANT_ADMINS.EVENTS;

      scope.$on(events.IDENTITY_PROVIDER_CREATED, function (event, data) {
        self.refreshDirectory(scope.tenant, type, data.directory).then(function () {
          scope.directories = self.directoriesFor(scope.tenant, type);
        });
      });

      scope.$on(events.IDENTITY_PROVIDER_UPDATED, function (event, data) {
        // No toastr — Azure-specific UX is NOT mirrored
        self.refreshDirectory(scope.tenant, type, data.directory).then(function () {
          scope.directories = self.directoriesFor(scope.tenant, type);
          scope.tenant.info.groupsEnabled = scope.tenant.isGroupsEnabled(scope.tenant.info);
        });
      });

      scope.$on(events.IDENTITY_PROVIDER_DELETED, function (event, data) {
        self.removeDirectory(scope.tenant, type, data.directory);
        scope.directories = self.directoriesFor(scope.tenant, type);
      });

      scope.$on(events.IDENTITY_PROVIDER_ERROR, function (event, data) {
        // Defensive envelope: backend may emit _ERROR data missing required fields;
        // fall back to a UNKNOWN_ERROR shape so REQUEST_ERROR_HELP consumers don't crash.
        const response = { data: null };
        try {
          const has = Object.prototype.hasOwnProperty;
          if (has.call(data, 'errorCode') &&
              has.call(data, 'parameterizedErrorText') &&
              has.call(data, 'parameters') &&
              has.call(data, 'errorText')) {
            response.data = data;
          } else {
            throw new Error('(data) is missing required attributes');
          }
        } catch (error) {
          response.data = {
            errorCode: 'UNKNOWN_ERROR',
            parameterizedErrorText: 'Unknown error',
            parameters: [],
            errorText: 'Unknown error'
          };
        }
        $rootScope.$broadcast('REQUEST_ERROR_HELP', { error: response });
      });

      // _WARNING NOT registered — semantics unverified; tracked under §5.9 backlog.
      // No return value — relies on AngularJS scope.$on auto-deregister via $scope.$destroy.
    };
  }

  IdpContextMenuMixin.$inject = ['$timeout'];

  function IdpContextMenuMixin($timeout) {
    return {
      attach: function (scope) {
        scope.openContextMenu = function (event, contextDescriptor, menuOption) {
          $timeout(function () {
            // Resolve formTemplate from descriptor for IdP-form menu types,
            // stash on the menu options object so the menu's isolate-scope
            // template can read it as `selectedTab.formTemplate` —
            // no new isolate-scope binding needed.
            if (menuOption && scope.descriptor &&
                menuOption.type === scope.descriptor.type) {
              menuOption.formTemplate = scope.descriptor.formTemplate;
            }
            scope.selectedContextMenuItem = menuOption;
            scope.contextPosition = { left: event.clientX, top: event.clientY };
            scope.formContext = contextDescriptor;
            scope.resource = scope.tenant;
          }, 50);
        };
        scope.closeAllMenues = function () {
          scope.selectedContextMenuItem = null;
          scope.contextPosition = null;
          scope.formContext = null;
          scope.resource = null;
        };
      }
    };
  }

})();
